import type { Dispatch } from '@reduxjs/toolkit';
import peopleApi from '@/store/api/peopleApi';
import savedContactsApi from '@/store/api/savedContactsApi';
import creditApi from '@/store/api/creditApi';
import type { TrackedBulkBatch } from '@/store/slices/bulkBatchesSlice';

/**
 * What happens when a BACKGROUND bulk batch finishes.
 *
 * The inline path (<=100 contacts) keeps itself current: every per-item SSE
 * finish schedules a debounced search refetch, and the screen that started the
 * run is still mounted to receive it. A background batch has neither - it emits
 * no per-item events at all, and it routinely outlives the screen that started
 * it (submit 500 contacts, go to Workspace, come back ten minutes later).
 *
 * Without this the unlocked values would only appear once RTK Query's cache
 * entry happened to expire, which requires the query to have NO subscribers:
 * `keepUnusedDataFor` counts down only while nothing is watching. A user who
 * submits a large batch and then simply sits on the All tab stays subscribed,
 * so their cache never expires and they watch locked rows indefinitely until
 * they touch a filter or change page. (has exactly this hole -
 * its useBulkContactsSocket.ts was written to close it and the invalidation
 * is still commented out.)
 *
 * Fixed by TAG INVALIDATION rather than a component callback, so it works
 * whatever screen the user is on:
 *  - a mounted query refetches immediately, so someone sitting on the All tab
 *    watches the rows unlock;
 *  - an unmounted one is marked stale and refetches when next subscribed, so
 *    returning to the tab later shows fresh data and costs no request now.
 *
 * BACKGROUND RUNS ONLY. Workflow-Service pushes the final `bulk.progress` for
 * every batch regardless of size, so inline runs reach this code path too and
 * are filtered out here - they already refresh themselves from their own
 * per-item events, and a second invalidation would be a duplicate search
 * request at the end of every small unlock.
 */

/**
 * peopleApi keeps its search tag module-private, so it is spelled out here. It
 * must stay in step with that module's NEGATIVE_SEARCH_TAG.
 */
const NEGATIVE_SEARCH_TAG = 'NegativeSearch';

/**
 * Batch keys already acted on. Module-level rather than component state
 * because the two callers are different hooks and either may observe a given
 * completion first - and because it has to survive the notification panel
 * (and so useBulkBatchTracking) unmounting in between.
 *
 * One short string per completed batch; a session would need tens of thousands
 * of batches before this is worth evicting from.
 */
const settledBatchKeys = new Set<string>();

/** Trailing debounce: a run's email and phone batches finish close together, and that's one refresh, not two. */
const REFRESH_DEBOUNCE_MS = 1500;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleContactsRefresh(dispatch: Dispatch) {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    // The All tab - one search endpoint backs it.
    dispatch(peopleApi.util.invalidateTags([NEGATIVE_SEARCH_TAG]));
    // The Saved tab.
    dispatch(savedContactsApi.util.invalidateTags(['SavedContacts']));
    // What the waterfall actually spent.
    dispatch(creditApi.util.invalidateTags(['CreditBalance', 'CreditHistory']));
  }, REFRESH_DEBOUNCE_MS);
}

/**
 * Whether a finished batch should refresh the CONTACT lists.
 *
 * Three ways to fall out:
 *  - not a background run: an inline unlock refreshes itself as it goes, so
 *    this would just duplicate its final refetch;
 *  - a Workspace or Enrichment batch: those write to a sheet, not to People
 *    Search, and run their own refresh (useWorkspaceBulkEvents' debounced rows
 *    refetch) - refreshing contacts for them is a wasted search request;
 *  - unknown to this client entirely: nothing to act on.
 *
 * Deliberately permissive about an unknown SOURCE on a known background batch:
 * a `bulk.progress` event carries no `source`, so a batch this session only
 * saw over SSE has none recorded. A needless refetch costs one request; a
 * missed one leaves the user staring at rows that are actually unlocked.
 */
function shouldRefreshContacts(batch: TrackedBulkBatch | undefined): boolean {
  if (!batch?.background) return false;
  return batch.source !== 'workspace' && batch.source !== 'enrichment';
}

/**
 * Marks one batch finished and, when it's a contacts batch, schedules the
 * refresh. Idempotent per batch key - the final SSE event and the list
 * endpoint report the same completion independently, and a duplicate or
 * replayed event must not re-trigger it.
 *
 * Returns true only the first time a given batch is reported.
 */
export function notifyBulkBatchCompleted(
  dispatch: Dispatch,
  batch: TrackedBulkBatch | undefined,
  batchKey: string
): boolean {
  if (!batchKey || settledBatchKeys.has(batchKey)) return false;
  settledBatchKeys.add(batchKey);
  if (shouldRefreshContacts(batch)) scheduleContactsRefresh(dispatch);
  return true;
}
